'use client';
import React from 'react';
import Link from 'next/link';
import { Users, DollarSign, TrendingUp, AlertTriangle, Shield, FileText, Activity, CheckCircle, Clock, XCircle, ArrowUpRight, ArrowDownRight, UserCheck, Wallet, Zap, Globe } from 'lucide-react';
import { AreaChart, Area, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';

const kpiCards = [
  { label: 'Total Users', value: '48,291', change: '+12.4%', up: true, icon: Users, color: '#7C3AED', bg: 'rgba(124,58,237,0.12)' },
  { label: 'Verified Users', value: '31,847', change: '+8.2%', up: true, icon: UserCheck, color: '#10B981', bg: 'rgba(16,185,129,0.12)' },
  { label: 'Daily Active', value: '9,412', change: '+5.1%', up: true, icon: Activity, color: '#06B6D4', bg: 'rgba(6,182,212,0.12)' },
  { label: 'Platform Revenue', value: '$84,320', change: '+18.7%', up: true, icon: DollarSign, color: '#F59E0B', bg: 'rgba(245,158,11,0.12)' },
  { label: 'Total Deposits', value: '$312,450', change: '+22.3%', up: true, icon: TrendingUp, color: '#A78BFA', bg: 'rgba(167,139,250,0.12)' },
  { label: 'Pending Withdrawals', value: '$18,920', change: '-3.1%', up: false, icon: Wallet, color: '#F43F5E', bg: 'rgba(244,63,94,0.12)' },
  { label: 'Fraud Alerts', value: '23', change: '+4', up: false, icon: AlertTriangle, color: '#F43F5E', bg: 'rgba(244,63,94,0.12)' },
  { label: 'KYC Pending', value: '156', change: '-12', up: true, icon: Shield, color: '#F59E0B', bg: 'rgba(245,158,11,0.12)' },
];

const revenueData = [
  { month: 'Feb', deposits: 210000, withdrawals: 145000, commission: 21000 },
  { month: 'Mar', deposits: 245000, withdrawals: 162000, commission: 24500 },
  { month: 'Apr', deposits: 198000, withdrawals: 130000, commission: 19800 },
  { month: 'May', deposits: 280000, withdrawals: 190000, commission: 28000 },
  { month: 'Jun', deposits: 305000, withdrawals: 210000, commission: 30500 },
  { month: 'Jul', deposits: 312450, withdrawals: 215000, commission: 31245 },
];

const userGrowthData = [
  { day: 'Mon', users: 320 }, { day: 'Tue', users: 480 }, { day: 'Wed', users: 390 },
  { day: 'Thu', users: 560 }, { day: 'Fri', users: 720 }, { day: 'Sat', users: 890 }, { day: 'Sun', users: 640 },
];

const recentAlerts = [
  { id: 'a1', type: 'fraud', msg: 'Multiple account detection — User #48291', time: '2m ago', severity: 'high' },
  { id: 'a2', type: 'kyc', msg: 'KYC document expired — User #31204', time: '15m ago', severity: 'medium' },
  { id: 'a3', type: 'withdrawal', msg: 'Large withdrawal request — $4,500 by User #22891', time: '32m ago', severity: 'high' },
  { id: 'a4', type: 'fraud', msg: 'Suspicious IP pattern — User #19034', time: '1h ago', severity: 'medium' },
  { id: 'a5', type: 'system', msg: 'Commission rule updated by Admin #2', time: '2h ago', severity: 'low' },
];

const quickLinks = [
  { label: 'User Management', href: '/admin/users', icon: Users, color: '#7C3AED' },
  { label: 'Activity Management', href: '/admin/activities', icon: Activity, color: '#06B6D4' },
  { label: 'Finance Tracking', href: '/admin/finance', icon: DollarSign, color: '#10B981' },
  { label: 'Fraud Alerts', href: '/admin/fraud', icon: AlertTriangle, color: '#F43F5E' },
  { label: 'KYC Queue', href: '/admin/kyc', icon: Shield, color: '#F59E0B' },
  { label: 'Audit Logs', href: '/admin/audit-logs', icon: FileText, color: '#A78BFA' },
];

const CustomTooltip = ({ active, payload, label }: any) => {
  if (active && payload && payload.length) {
    return (
      <div className="rounded-lg p-3 text-xs" style={{ background: '#1E293B', border: '1px solid #1E2A4A' }}>
        <p className="font-semibold mb-1" style={{ color: '#F1F5F9' }}>{label}</p>
        {payload.map((p: any) => (
          <p key={p.name} style={{ color: p.color }}>{p.name}: ${(p.value / 1000).toFixed(1)}k</p>
        ))}
      </div>
    );
  }
  return null;
};

export default function AdminDashboardContent() {
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Admin Dashboard</h1>
          <p className="text-sm mt-0.5" style={{ color: 'var(--muted-foreground)' }}>
            Brain Basket Control Center — Full system overview
          </p>
        </div>
        <div className="flex items-center gap-2">
          <span className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full font-semibold"
            style={{ background: 'rgba(16,185,129,0.15)', color: '#10B981', border: '1px solid rgba(16,185,129,0.3)' }}>
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse inline-block" />
            All Systems Operational
          </span>
        </div>
      </div>

      {/* KPI Grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {kpiCards.map((card) => (
          <div key={card.label} className="rounded-xl p-4 card-hover"
            style={{ background: 'var(--card)', border: '1px solid var(--border)' }}>
            <div className="flex items-start justify-between mb-3">
              <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ background: card.bg }}>
                <card.icon size={18} style={{ color: card.color }} />
              </div>
              <span className={`text-xs font-semibold flex items-center gap-0.5 ${card.up ? 'text-emerald-400' : 'text-rose-400'}`}>
                {card.up ? <ArrowUpRight size={12} /> : <ArrowDownRight size={12} />}
                {card.change}
              </span>
            </div>
            <p className="text-xl font-bold text-foreground">{card.value}</p>
            <p className="text-xs mt-0.5" style={{ color: 'var(--muted-foreground)' }}>{card.label}</p>
          </div>
        ))}
      </div>

      {/* Quick Links */}
      <div className="grid grid-cols-3 md:grid-cols-6 gap-3">
        {quickLinks.map((link) => (
          <Link key={link.label} href={link.href}
            className="flex flex-col items-center gap-2 p-3 rounded-xl text-center card-hover"
            style={{ background: 'var(--card)', border: '1px solid var(--border)' }}>
            <div className="w-10 h-10 rounded-xl flex items-center justify-center"
              style={{ background: `${link.color}20` }}>
              <link.icon size={20} style={{ color: link.color }} />
            </div>
            <span className="text-xs font-medium leading-tight" style={{ color: 'var(--muted-foreground)' }}>{link.label}</span>
          </Link>
        ))}
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Revenue Chart */}
        <div className="lg:col-span-2 rounded-xl p-5" style={{ background: 'var(--card)', border: '1px solid var(--border)' }}>
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="text-sm font-semibold text-foreground">Revenue Overview</h3>
              <p className="text-xs mt-0.5" style={{ color: 'var(--muted-foreground)' }}>Deposits, Withdrawals & Commission</p>
            </div>
            <span className="text-xs px-2 py-1 rounded-md" style={{ background: 'var(--muted)', color: 'var(--muted-foreground)' }}>Last 6 months</span>
          </div>
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={revenueData} barGap={4}>
              <CartesianGrid strokeDasharray="3 3" stroke="#1E2A4A" vertical={false} />
              <XAxis dataKey="month" tick={{ fill: '#64748B', fontSize: 11 }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fill: '#64748B', fontSize: 11 }} axisLine={false} tickLine={false} tickFormatter={(v) => `$${v / 1000}k`} />
              <Tooltip content={<CustomTooltip />} />
              <Bar dataKey="deposits" name="Deposits" fill="#7C3AED" radius={[4, 4, 0, 0]} />
              <Bar dataKey="withdrawals" name="Withdrawals" fill="#06B6D4" radius={[4, 4, 0, 0]} />
              <Bar dataKey="commission" name="Commission" fill="#F59E0B" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
          <div className="flex items-center gap-4 mt-3">
            {[{ label: 'Deposits', color: '#7C3AED' }, { label: 'Withdrawals', color: '#06B6D4' }, { label: 'Commission', color: '#F59E0B' }].map(l => (
              <div key={l.label} className="flex items-center gap-1.5">
                <span className="w-2.5 h-2.5 rounded-sm" style={{ background: l.color }} />
                <span className="text-xs" style={{ color: 'var(--muted-foreground)' }}>{l.label}</span>
              </div>
            ))}
          </div>
        </div>

        {/* User Growth */}
        <div className="rounded-xl p-5" style={{ background: 'var(--card)', border: '1px solid var(--border)' }}>
          <div className="mb-4">
            <h3 className="text-sm font-semibold text-foreground">New Users This Week</h3>
            <p className="text-2xl font-bold mt-1 text-foreground">4,600</p>
            <span className="text-xs text-emerald-400 flex items-center gap-0.5 mt-0.5">
              <ArrowUpRight size={12} /> +18.2% vs last week
            </span>
          </div>
          <ResponsiveContainer width="100%" height={140}>
            <AreaChart data={userGrowthData}>
              <defs>
                <linearGradient id="userGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#7C3AED" stopOpacity={0.3} />
                  <stop offset="95%" stopColor="#7C3AED" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#1E2A4A" vertical={false} />
              <XAxis dataKey="day" tick={{ fill: '#64748B', fontSize: 10 }} axisLine={false} tickLine={false} />
              <Tooltip contentStyle={{ background: '#1E293B', border: '1px solid #1E2A4A', borderRadius: 8, fontSize: 11 }} />
              <Area type="monotone" dataKey="users" stroke="#7C3AED" strokeWidth={2} fill="url(#userGrad)" />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Alerts & Recent Activity */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Recent Alerts */}
        <div className="rounded-xl p-5" style={{ background: 'var(--card)', border: '1px solid var(--border)' }}>
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-semibold text-foreground">Recent Alerts</h3>
            <Link href="/admin/fraud" className="text-xs font-semibold" style={{ color: '#A78BFA' }}>View all</Link>
          </div>
          <div className="space-y-3">
            {recentAlerts.map((alert) => (
              <div key={alert.id} className="flex items-start gap-3 p-3 rounded-lg"
                style={{ background: 'var(--muted)', border: '1px solid var(--border)' }}>
                <div className={`w-2 h-2 rounded-full mt-1.5 flex-shrink-0 ${alert.severity === 'high' ? 'bg-rose-400' : alert.severity === 'medium' ? 'bg-amber-400' : 'bg-emerald-400'}`} />
                <div className="flex-1 min-w-0">
                  <p className="text-xs text-foreground leading-snug">{alert.msg}</p>
                  <p className="text-xs mt-0.5" style={{ color: 'var(--muted-foreground)' }}>{alert.time}</p>
                </div>
                <span className={`text-xs px-2 py-0.5 rounded-full font-semibold flex-shrink-0 ${
                  alert.severity === 'high' ? 'bg-rose-500/15 text-rose-400' :
                  alert.severity === 'medium' ? 'bg-amber-500/15 text-amber-400' : 'bg-emerald-500/15 text-emerald-400'
                }`}>{alert.severity}</span>
              </div>
            ))}
          </div>
        </div>

        {/* System Stats */}
        <div className="rounded-xl p-5" style={{ background: 'var(--card)', border: '1px solid var(--border)' }}>
          <h3 className="text-sm font-semibold text-foreground mb-4">System Health</h3>
          <div className="space-y-3">
            {[
              { label: 'Active Competitions', value: '1,284', icon: Zap, color: '#7C3AED' },
              { label: 'Completed Today', value: '3,891', icon: CheckCircle, color: '#10B981' },
              { label: 'Pending Disputes', value: '14', icon: Clock, color: '#F59E0B' },
              { label: 'Locked Funds', value: '$42,180', icon: Wallet, color: '#06B6D4' },
              { label: 'Countries Active', value: '38', icon: Globe, color: '#A78BFA' },
              { label: 'Banned Accounts', value: '89', icon: XCircle, color: '#F43F5E' },
            ].map((stat) => (
              <div key={stat.label} className="flex items-center justify-between py-2 border-b last:border-0"
                style={{ borderColor: 'var(--border)' }}>
                <div className="flex items-center gap-2">
                  <stat.icon size={14} style={{ color: stat.color }} />
                  <span className="text-sm" style={{ color: 'var(--muted-foreground)' }}>{stat.label}</span>
                </div>
                <span className="text-sm font-semibold text-foreground">{stat.value}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
